import { useCallback, useRef } from 'react';
import { CHATBOT_STATUS } from '../constants/status';
import { parseToolEvents } from '../utils/parsers/toolEventParser';

/**
 * Custom hook for streaming response handlers
 * Extracts the complex streaming logic from useChatConversation
 */
export function useStreamingHandlers({
  setConversationStatus,
  setStreamingMessage,
  addMessage,
  setActiveToolEvents,
  newlyCreatedConversationRef,
  cleanupStreamingResources,
  onStreamComplete,
  clearStreamingCanvas,
  mode,
}) {
  // Track if we've received the first chunk for the current stream
  const isFirstChunkRef = useRef(true);

  const handleChunk = useCallback(
    (chunk) => {
      // Set status to STREAMING on first chunk
      if (isFirstChunkRef.current) {
        setConversationStatus(CHATBOT_STATUS.STREAMING);
        isFirstChunkRef.current = false;
      }

      // Clear canvas streaming when receiving assistant delta in teacher-tools mode
      // This handles the canvas_delta → assistant_delta transition
      if (mode === 'teacher-tools' && clearStreamingCanvas) {
        clearStreamingCanvas();
      }

      // Parse tool events from the chunk
      const { cleanText, events } = parseToolEvents(chunk);

      // Filter out reference markers to prevent showing raw JSON during streaming
      const displayText = cleanText.replace(/\[CONTENT_REFERENCES\][\s\S]*?\[\/CONTENT_REFERENCES\]/g, '');

      // Process tool events
      events.forEach((event) => {
        if (event.status === 'started') {
          setActiveToolEvents((prev) => {
            // Check if a tool with the same name already exists
            const existingToolIndex = prev.findIndex((e) => e.toolName === event.toolName);

            if (existingToolIndex >= 0) {
              // Update existing tool event instead of adding a new one
              const updated = [...prev];
              updated[existingToolIndex] = { ...event, id: prev[existingToolIndex].id };
              return updated;
            } else {
              // Add new tool event
              return [...prev, { ...event, id: `${event.toolName}-${Date.now()}` }];
            }
          });
        } else if (event.status === 'completed' || event.status === 'failed') {
          setActiveToolEvents((prev) => prev.map((e) => (e.toolName === event.toolName ? { ...e, status: event.status, result: event.result } : e)));
        }
      });

      // Use functional update to avoid stale state issues
      setStreamingMessage((prev) => {
        // Skip update if text is the same to avoid unnecessary rerenders
        if (prev && prev.text === displayText) {
          return prev;
        }
        return {
          ...prev,
          text: displayText,
        };
      });
    },
    [setActiveToolEvents, setStreamingMessage, setConversationStatus, clearStreamingCanvas, mode]
  );

  const handleComplete = useCallback(
    (fullText, botMessageId, globalChatContext, conversationForMessage, realMessageId = null) => {
      // Reset first chunk flag for next stream
      isFirstChunkRef.current = true;

      // Clean up streaming resources
      cleanupStreamingResources();

      setConversationStatus(CHATBOT_STATUS.COMPLETE);

      // Transition to IDLE after a brief delay to show completion state
      setTimeout(() => {
        setConversationStatus(CHATBOT_STATUS.IDLE);
      }, 100);

      // Parse and clean final text from any remaining tool events
      const { cleanText } = parseToolEvents(fullText);

      // Use real message ID from backend if available, otherwise fall back to temp ID
      const finalMessageId = realMessageId || botMessageId;

      // Update the streaming message with final text
      const finalMessage = {
        id: finalMessageId,
        text: cleanText,
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: false,
        context: globalChatContext,
      };

      // Replace streaming message with final message
      setStreamingMessage(null);
      addMessage(finalMessage);

      // Clear the newly created conversation tracking since we now have messages
      if (newlyCreatedConversationRef.current === conversationForMessage) {
        newlyCreatedConversationRef.current = null;
      }

      // Clear active tool events after a short delay to show completion
      setTimeout(() => {
        setActiveToolEvents([]);
      }, 1000);

      // Notify that streaming is complete for this conversation
      if (onStreamComplete && conversationForMessage) {
        onStreamComplete(conversationForMessage);
      }
    },
    [cleanupStreamingResources, setConversationStatus, setStreamingMessage, addMessage, setActiveToolEvents, newlyCreatedConversationRef, onStreamComplete]
  );

  const handleError = useCallback((error, currentConversationId, setStreamingMessage, setConversationStatus, streamControllerRef) => {
    // Check if this is a conversation creation error
    const isConversationCreationError = error?.message?.includes('conversation') || error?.message?.includes('Failed to create') || !currentConversationId;

    // Set error state
    setConversationStatus(CHATBOT_STATUS.ERROR);
    streamControllerRef.current = null;

    // Extract error message if available
    let errorMessage = 'Sorry, there was an error processing your request.';

    // Customize message based on error type
    if (isConversationCreationError) {
      errorMessage = 'Unable to create conversation. Please try again later.';
    } else if (error && error.message) {
      errorMessage = `Error: ${error.message}`;
    }

    // Update UI to show error
    setStreamingMessage((prev) => ({
      ...prev,
      text: errorMessage,
      isError: true,
      isStreaming: false,
    }));

    return { isConversationCreationError };
  }, []);

  return {
    handleChunk,
    handleComplete,
    handleError,
  };
}
